/**
 * Pure helpers for user first/last name handling on the frontend.
 *
 * Mirrors lambda-functions/utils/name_utils.py so the form and the backend
 * agree on how a single legacy `name` is split and how First/Last compose.
 */

export const MAX_NAME_LENGTH = 100;

// Reject angle brackets and control characters (basic XSS guard, matches backend).
// eslint-disable-next-line no-control-regex
const INVALID_NAME_RE = /[<>\u0000-\u001f\u007f]/;

function collapseWhitespace(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').trim();
}

/** Compose the single display name from first + last (trim + collapse spaces). */
export function composeName(first: string, last: string): string {
  return `${collapseWhitespace(first)} ${collapseWhitespace(last)}`.trim();
}

/**
 * Best-effort split of a single name into [first, last].
 * Last = final token; First = everything before it.
 */
export function splitName(full: string | null | undefined): [string, string] {
  const tokens = collapseWhitespace(full).split(' ').filter(Boolean);
  if (tokens.length === 0) return ['', ''];
  if (tokens.length === 1) return [tokens[0], ''];
  return [tokens.slice(0, -1).join(' '), tokens[tokens.length - 1]];
}

/** True when value is an acceptable name part (non-empty, bounded, no < or >). */
export function isValidName(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  const trimmed = String(value).trim();
  if (!trimmed) return false;
  if (trimmed.length > MAX_NAME_LENGTH) return false;
  if (INVALID_NAME_RE.test(trimmed)) return false;
  return true;
}
